/**
 * ProjectLifecycle — owner-side lifecycle hub for a single project.
 *
 * Route: /project/:projectId/lifecycle
 *
 * One page with three panels:
 *   1. Job Site Rules editor — add/edit/delete rules surfaced in award emails
 *   2. Cascade Timeline — chronological audit log of every email/event fired
 *   3. Contractor roster — quick "View as Contractor" buttons + Send-cascade
 *
 * This avoids surgery on the 3000-line Project Hub file; we link here from a
 * single button in the Project Hub header.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Save, ShieldCheck, Mail, ExternalLink,
  Activity, RefreshCw, AlertTriangle, Users, ScrollText, Eye, Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import CascadeTestModeBanner from '@/components/CascadeTestModeBanner';

const RULE_CATEGORIES = [
  'Safety', 'Parking & Access', 'Site Cleanup', 'Hours of Operation',
  'Communication', 'Materials & Deliveries', 'Other',
];

const fmtTime = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return iso; }
};

// ============================ JOB SITE RULES =============================
const JobSiteRulesPanel = ({ projectId }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/projects/${projectId}/job-site-rules`);
      setRules(res.data || []);
      setDirty(false);
    } catch (e) {
      toast.error('Could not load rules');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const addRule = () => {
    setRules([...rules, { id: `local-${Date.now()}`, category: 'Other', title: '', body: '' }]);
    setDirty(true);
  };
  const updateRule = (idx, patch) => {
    const next = rules.slice();
    next[idx] = { ...next[idx], ...patch };
    setRules(next);
    setDirty(true);
  };
  const removeRule = (idx) => {
    const next = rules.slice();
    next.splice(idx, 1);
    setRules(next);
    setDirty(true);
  };
  const save = async () => {
    // strip empty rows
    const clean = rules.filter((r) => (r.title || '').trim());
    if (rules.length !== clean.length) {
      setRules(clean);
    }
    setSaving(true);
    try {
      await api.put(`/projects/${projectId}/job-site-rules`, { rules: clean });
      toast.success(`Saved ${clean.length} rule${clean.length === 1 ? '' : 's'}`);
      setDirty(false);
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-blue-600" />
            Job Site Rules
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Surfaced in every award email and the contractor workspace.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={addRule} data-testid="add-rule"><Plus className="w-3 h-3 mr-1" />Add rule</Button>
          <Button size="sm" onClick={save} disabled={!dirty || saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-rules">
            <Save className="w-3 h-3 mr-1" />{saving ? 'Saving…' : (dirty ? 'Save' : 'Saved')}
          </Button>
        </div>
      </div>
      {loading && <p className="text-xs text-slate-500 text-center py-6">Loading…</p>}
      {!loading && rules.length === 0 && (
        <p className="text-xs text-slate-500 text-center py-6">No rules yet. Add a few — they'll be auto-included in every award email.</p>
      )}
      <div className="space-y-2">
        {rules.map((r, idx) => (
          <div key={r.id} className="grid grid-cols-12 gap-2 border border-slate-200 rounded p-2.5 bg-slate-50" data-testid={`rule-${idx}`}>
            <div className="col-span-3">
              <Select value={r.category} onValueChange={(v) => updateRule(idx, { category: v })}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RULE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-8">
              <Input
                placeholder="Rule title (e.g., Park on north side only)"
                value={r.title}
                onChange={(e) => updateRule(idx, { title: e.target.value })}
                className="h-8 text-xs"
                data-testid={`rule-title-${idx}`}
              />
              <Textarea
                placeholder="Details (optional)"
                value={r.body || ''}
                onChange={(e) => updateRule(idx, { body: e.target.value })}
                className="mt-1.5 text-xs min-h-[44px]"
                data-testid={`rule-body-${idx}`}
              />
            </div>
            <div className="col-span-1 flex items-start justify-end">
              <button onClick={() => removeRule(idx)} className="text-red-500 hover:text-red-700 mt-1" data-testid={`rule-del-${idx}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

// ========================== CASCADE TIMELINE =============================
const KIND_LABEL = {
  award_email:    { label: 'Award email sent',    icon: Mail,       tone: 'text-blue-600 bg-blue-100' },
  kickoff_email:  { label: 'Kickoff scheduled',   icon: Mail,       tone: 'text-blue-600 bg-blue-100' },
  portal_link:    { label: 'Portal access ready', icon: ShieldCheck,tone: 'text-emerald-600 bg-emerald-100' },
  cadence_ping:   { label: '10-day check-in',     icon: Zap,        tone: 'text-amber-600 bg-amber-100' },
  schedule_shift: { label: 'Schedule shifted',    icon: RefreshCw,  tone: 'text-amber-600 bg-amber-100' },
  manual_note:    { label: 'Note',                icon: Activity,   tone: 'text-slate-600 bg-slate-100' },
};

const CascadeTimelinePanel = ({ projectId }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/projects/${projectId}/cascade-timeline`);
      setEvents(res.data || []);
    } catch (e) { toast.error('Could not load timeline'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            <Activity className="w-4 h-4 text-blue-600" />
            Cascade Timeline
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Every email and schedule event fired on this project.</p>
        </div>
        <Button size="sm" variant="outline" onClick={load} data-testid="reload-timeline">
          <RefreshCw className="w-3 h-3 mr-1" />Refresh
        </Button>
      </div>
      {loading && <p className="text-xs text-slate-500 text-center py-6">Loading…</p>}
      {!loading && events.length === 0 && (
        <p className="text-xs text-slate-500 text-center py-6">No events yet. Award a contractor and the cascade fills this in.</p>
      )}
      <div className="space-y-2">
        {events.map((e) => {
          const cfg = KIND_LABEL[e.kind] || { label: e.kind, icon: Activity, tone: 'text-slate-600 bg-slate-100' };
          const Icon = cfg.icon;
          const failed = e.status === 'failed';
          return (
            <div key={e.id} className={`flex gap-3 border rounded p-2.5 ${failed ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`} data-testid={`event-${e.id}`}>
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${cfg.tone}`}>
                <Icon className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-slate-900">{cfg.label}</p>
                  <span className="text-[10px] text-slate-500">{fmtTime(e.created_at)}</span>
                </div>
                {e.subject && <p className="text-[11px] text-slate-700 mt-0.5">{e.subject}</p>}
                {e.summary && <p className="text-[10px] text-slate-500 mt-0.5">{e.summary}</p>}
                {failed && <p className="text-[10px] text-red-700 mt-1 font-semibold">⚠️ Send failed — check SendGrid logs</p>}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// =========================== CONTRACTOR ROSTER ===========================
const ContractorRosterPanel = ({ projectId, project, contractors }) => {
  const navigate = useNavigate();
  if (!contractors || contractors.length === 0) {
    return (
      <Card className="p-4">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-blue-600" />
          Contractors on this project
        </h3>
        <p className="text-xs text-slate-500 text-center py-6">No contractors assigned yet.</p>
      </Card>
    );
  }
  return (
    <Card className="p-4">
      <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-blue-600" />
        Contractors on this project
      </h3>
      <div className="space-y-2">
        {contractors.map((c) => {
          const workspaceUrl = `/contractor-workspace?email=${encodeURIComponent(c.email || '')}&project_id=${projectId}`;
          return (
            <div key={c.id} className="border border-slate-200 rounded p-3 flex items-center justify-between gap-2 bg-white">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-900 truncate">{c.name}</p>
                <p className="text-[10px] text-slate-500 truncate">{c.trade || '—'} · {c.email || 'no email'}</p>
              </div>
              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(workspaceUrl, '_blank')}
                  disabled={!c.email}
                  title={!c.email ? 'No email on file' : 'Preview their workspace'}
                  data-testid={`view-as-${c.id}`}
                >
                  <Eye className="w-3 h-3 mr-1" />
                  View as
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

// ============================== PAGE =====================================
export default function ProjectLifecycle({ user }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [projRes, contractorsRes] = await Promise.all([
          api.get(`/projects/${projectId}`),
          user?.id ? api.get(`/contractors?user_id=${user.id}`) : Promise.resolve({ data: [] }),
        ]);
        if (!alive) return;
        setProject(projRes.data);
        // filter contractors associated with this project (best effort)
        const all = contractorsRes.data || [];
        // For now show all owner's contractors — refining the filter requires
        // knowing how this project's contractors are linked (phases, bids, etc.)
        setContractors(all);
      } catch (e) {
        toast.error('Could not load project');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [projectId, user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500 text-sm">Loading lifecycle…</div>;
  if (!project) return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <Card className="p-8 max-w-md text-center">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <h2 className="text-lg font-bold mb-2">Project not found</h2>
        <Button onClick={() => navigate('/dashboard')} variant="outline" className="mt-3">Back to dashboard</Button>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 px-4 sm:px-6 py-5" data-testid="project-lifecycle-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <button
              onClick={() => navigate(`/project/${projectId}`)}
              className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 mb-1"
              data-testid="back-to-project"
            >
              <ArrowLeft className="w-3 h-3" />
              Back to project
            </button>
            <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600">Contractor Lifecycle</p>
            <h1 className="text-xl font-bold text-slate-900">{project.name}</h1>
            <p className="text-xs text-slate-500">{project.address || ''}</p>
          </div>
        </div>

        <CascadeTestModeBanner user={user} compact />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-4">
            <JobSiteRulesPanel projectId={projectId} />
            <ContractorRosterPanel projectId={projectId} project={project} contractors={contractors} />
          </div>
          <div>
            <CascadeTimelinePanel projectId={projectId} />
          </div>
        </div>
      </div>
    </div>
  );
}
